import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardBody,
  IconLock,
  Label,
  mergeClasses
} from "@busbud/horizon";

import { getLocalizedPrice } from "@app/components/localized-price";
import { useAppContext } from "@app/helpers/hooks";
import { LocalStorage } from "@app/lib/bowser-storage/local-storage";
import { ClickedPriceLockSmokeTest } from "@app/tracking/results-tracking";

const local_storage_selected_key = "price-lock-smoke-test-selected";

function hasInteractedWithComponent() {
  const local_storage = LocalStorage.fromWindow(window);
  return local_storage.getItem(local_storage_selected_key) as string | null;
}

const PRICE_LOCK_RATE = 0.05;

export const PriceLockSmokeTest: React.FC<{
  price_in_cents: number | undefined;
}> = ({ price_in_cents }) => {
  const { tracker, translator, features, device, locale, intlProvider } =
    useAppContext();
  const [has_voted, setHasVoted] = useState(false);
  const [display_component, setDisplayComponent] = useState(
    features.PRICE_LOCK_SMOKE_TEST
  );

  // should have been a useCallback but window prevents from running on server
  useEffect(() => {
    setDisplayComponent(
      features.PRICE_LOCK_SMOKE_TEST && hasInteractedWithComponent() === null
    );
  }, []);

  const mobile_classes = device.is_mobile ? "p-200 pt-0" : "";
  const background_color = has_voted
    ? "bg-color-static-transparent"
    : "bg-color-scheme-literal-purple-200";

  const clickedPriceLock = useCallback(() => {
    const local_storage = LocalStorage.fromWindow(window);

    local_storage.setItem(local_storage_selected_key, JSON.stringify(true));
    tracker.track(ClickedPriceLockSmokeTest());
    setHasVoted(true);
  }, [tracker]);

  if (!price_in_cents || !display_component) {
    return null;
  }

  return (
    <div className={mobile_classes}>
      <Card
        elevation="sunken"
        className={mergeClasses(
          "border-width-sm border-color-scheme-literal-purple-200 duration-smooth",
          background_color
        )}
      >
        <CardBody className="flex flex-col justify-between gap-200 md:flex-row">
          {!!has_voted ? (
            <div className="flex min-w-[110px] items-center justify-center text-center lg:min-w-[145px]">
              <Label
                as="span"
                size="sm"
                fontWeight="extraBold"
                className="text-color-tertiary"
              >
                {translator.t(
                  "!results.price-lock-smoke-test.feature-unavailable"
                )}
              </Label>
            </div>
          ) : (
            <>
              <div>
                <Label
                  as="span"
                  size="lg"
                  fontWeight="extraBold"
                  className="text-color-primary"
                >
                  {translator.t("!results.price-lock-smoke-test.title")}
                </Label>
                <Label as="span" size="lg" className="text-color-primary">
                  {translator.t("!results.price-lock-smoke-test.content", {
                    price: getLocalizedPrice(
                      {
                        price_in_cents: price_in_cents * PRICE_LOCK_RATE
                      },
                      { locale, intlProvider }
                    )
                  })}
                </Label>
              </div>
              <Button
                ariaLabel={translator.t("!results.price-lock-smoke-test.cta")}
                className="w-full flex-shrink-0 self-center md:w-auto"
                onClick={() => clickedPriceLock()}
                iconStart={<IconLock size="md" />}
              >
                {translator.t("!results.price-lock-smoke-test.cta")}
              </Button>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
